import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { LandingFooter } from './components/LandingFooter'
import { SorteeLandingSeo } from './components/SorteeLandingSeo'
import { CampaignScene } from './components/CampaignScene'
import { ProductPreview } from './components/ProductPreview'
import { EmphasizedCopy } from './components/EmphasizedCopy'
import { EngagementWheel } from './components/EngagementWheel'
import { ArrowRightIcon, LandingIcon } from './components/icons'
import {
  premiumFaq,
  premiumFeatures,
  premiumSteps,
} from './content/premiumLandingContent'
import {
  sorteeLandingFooter,
  sorteeLandingHero,
  sorteeLandingFeaturesHeader,
  sorteeLandingHow,
  sorteeLandingFaq,
  sorteeLandingCta,
  sorteeLandingMarqueeItems,
} from './content/sorteeLandingContent'
import {
  RestoredProof,
  RestoredTestimonials,
  RestoredDashboard,
  RestoredFeatureDetails,
  RestoredStepDemo,
  RestoredHeroTrust,
} from './components/RestoredContent'
import styles from './SorteeLandingPage.module.css'
import './styles/sortee-landing-scope.css'

export function SorteeLandingPage() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)
  const menuButton = useRef<HTMLButtonElement>(null)
  const page = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && menuOpen) {
        setMenuOpen(false)
        menuButton.current?.focus()
      }
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [menuOpen])

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.setAttribute('data-visible', 'true')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.12 }
    )
    page.current
      ?.querySelectorAll('[data-reveal]')
      .forEach((element) => observer.observe(element))
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={page} className={styles.page}>
      <SorteeLandingSeo />
      <a href="#conteudo" className={styles.skipLink}>
        Pular para o conteúdo
      </a>
      <header className={styles.header}>
        <div className={styles.navInner}>
          <Link className={styles.brand} to="/" aria-label="Sortee, página inicial">
            <img
              className={styles.brandLogo}
              src="/logo/sortee.png"
              alt="Logo Sortee"
              width="2089"
              height="753"
            />
          </Link>
          <nav className={styles.desktopNav} aria-label="Navegação principal">
            <Link to="/recursos">Recursos</Link>
            <a href="#features">Funcionalidades</a>
            <a href="#how">Como funciona</a>
            <a href="#faq">FAQ</a>
          </nav>
          <div className={styles.navActions}>
            <Link className={styles.login} to="/login">
              Entrar
            </Link>
            <Link className={styles.navCta} to="/cadastro">
              Começar grátis <ArrowRightIcon />
            </Link>
            <button
              ref={menuButton}
              className={styles.menuButton}
              type="button"
              aria-expanded={menuOpen}
              aria-controls="mobile-navigation"
              aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <span className={styles.menuGlyph} aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </button>
          </div>
        </div>
        <nav
          id="mobile-navigation"
          aria-hidden={!menuOpen}
          inert={!menuOpen}
          data-open={menuOpen}
          className={styles.mobileNav}
          aria-label="Navegação mobile"
          onClick={() => setMenuOpen(false)}
        >
          <div className={styles.mobileNavContent}>
            <Link to="/recursos">
              Recursos <ArrowRightIcon />
            </Link>
            <a href="#features">
              Funcionalidades <LandingIcon name="diagonal" />
            </a>
            <a href="#how">
              Como funciona <LandingIcon name="diagonal" />
            </a>
            <a href="#faq">
              FAQ <LandingIcon name="diagonal" />
            </a>
            <Link to="/login">
              Entrar na minha conta <LandingIcon name="diagonal" />
            </Link>
          </div>
        </nav>
      </header>
      <main id="conteudo" tabIndex={-1}>
        <section
          className={`${styles.container} ${styles.hero}`}
          aria-labelledby="hero-title"
        >
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>
              <span className={styles.statusDot} /> PLATAFORMA PARA CRIAR E GERENCIAR
              SORTEIOS
            </p>
            <h1 id="hero-title">
              {sorteeLandingHero.headlineLineOne}
              <br />
              de{' '}
              <span
                className={styles.cycleWords}
                aria-label="sorteios, gamificação, resultados"
              >
                <span aria-hidden="true">
                  {sorteeLandingHero.headlineCycleWords.map((word, index) => (
                    <em key={`${word}-${index}`}>{word}</em>
                  ))}
                </span>
              </span>
              <br />
              {sorteeLandingHero.headlineLineThree}
            </h1>
            <p className={styles.heroDescription}>
              {sorteeLandingHero.descriptionPrefix}
              <strong>{sorteeLandingHero.descriptionHighlight}</strong>
              {sorteeLandingHero.descriptionSuffix}
            </p>
            <div className={styles.heroActions}>
              <Link className={styles.primaryButton} to="/cadastro">
                {sorteeLandingHero.primaryActionLabel} <ArrowRightIcon />
              </Link>
              <a className={styles.textButton} href="#how">
                {sorteeLandingHero.secondaryActionLabel} <ArrowRightIcon />
              </a>
            </div>
            <div className={styles.heroNote}>
              <LandingIcon name="check" width="14" height="14" />
              <strong>{sorteeLandingHero.trustStrongLabel}</strong>
              {sorteeLandingHero.trustSuffixLabel}
            </div>
            <RestoredHeroTrust />
          </div>
          <CampaignScene />
          <div className={styles.heroBottom}>
            <span>DA PRIMEIRA IDEIA AO GRANDE MOMENTO.</span>
            <a href="#features">
              Conheça os recursos <LandingIcon name="down" />
            </a>
          </div>
        </section>
        <RestoredProof />
        <section className={styles.capabilities} aria-labelledby="capabilities-title">
          <div className={styles.container}>
            <p className={styles.capabilitiesLabel}>RECURSOS DA PLATAFORMA</p>
            <h2 id="capabilities-title" className={styles.capabilitiesTitle}>
              Tudo o que sua campanha precisa.
            </h2>
            <ul className={styles.capabilitiesList}>
              {sorteeLandingMarqueeItems.map((title) => (
                <li key={title}>
                  <LandingIcon name="check" />
                  <div>
                    <h3>{title}</h3>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
        <section
          id="features"
          className={`${styles.container} ${styles.section}`}
          aria-labelledby="features-title"
        >
          <div className={styles.sectionHeading} data-reveal>
            <div>
              <p className={styles.eyebrow}>{sorteeLandingFeaturesHeader.tag}</p>
              <h2 id="features-title">
                {sorteeLandingFeaturesHeader.headingLineOne}
                <br />
                <em>{sorteeLandingFeaturesHeader.headingLineTwo}</em>
              </h2>
            </div>
            <p>{sorteeLandingFeaturesHeader.sideText}</p>
          </div>
          <div className={styles.featureGrid}>
            {premiumFeatures.map((feature, index) => (
              <article key={feature.title} className={styles.feature} data-reveal>
                <div className={styles.featureTop}>
                  <span className={styles.featureIcon} aria-hidden="true">
                    <LandingIcon name={feature.icon} width="24" height="24" />
                  </span>
                  <span>0{index + 1}</span>
                </div>
                <h3>{feature.title}</h3>
                <p>
                  <EmphasizedCopy
                    text={feature.description}
                    highlight={feature.highlight}
                  />
                </p>
                <RestoredFeatureDetails featureKey={feature.key} />
                <span className={styles.featureTag}>{feature.tag}</span>
              </article>
            ))}
          </div>
        </section>
        <section
          id="plataforma"
          className={styles.productSection}
          aria-labelledby="product-title"
        >
          <div className={styles.container}>
            <div className={styles.sectionHeading} data-reveal>
              <div>
                <p className={styles.eyebrow}>Dashboard</p>
                <h2 id="product-title">
                  Dashboard em
                  <br />
                  <em>tempo real.</em>
                </h2>
              </div>
              <p>
                Saiba o que está acontecendo.
                <br />
                <strong>Acompanhe os resultados</strong> e dê o próximo passo com
                informação.
              </p>
            </div>
            <ProductPreview original />
            <RestoredDashboard />
            <div className={styles.productFootnote}>
              <span>
                <span className={styles.statusDot} /> UMA VISÃO COMPLETA DA OPERAÇÃO
              </span>
              <span>Painel de demonstração · dados fictícios</span>
            </div>
          </div>
        </section>
        <section
          id="how"
          className={`${styles.container} ${styles.section}`}
          aria-labelledby="how-title"
        >
          <div className={styles.sectionHeading} data-reveal>
            <div>
              <p className={styles.eyebrow}>{sorteeLandingHow.tag}</p>
              <h2 id="how-title">
                {sorteeLandingHow.headingLineOne}
                <br />
                em <em>{sorteeLandingHow.headingLineTwo}</em>
              </h2>
            </div>
            <Link className={styles.textButton} to="/cadastro">
              Vamos começar <ArrowRightIcon />
            </Link>
          </div>
          <div className={styles.steps}>
            {premiumSteps.map((step, index) => (
              <article className={styles.step} key={step.title} data-reveal>
                <span className={styles.stepNumber}>0{index + 1}</span>
                <h3>{step.title}</h3>
                <p>
                  <EmphasizedCopy text={step.description} highlight={step.highlight} />
                </p>
                <span className={styles.stepDetail}>{step.detail}</span>
                <RestoredStepDemo index={index} />
              </article>
            ))}
          </div>
        </section>
        <section
          className={`${styles.container} ${styles.engagement}`}
          aria-labelledby="engagement-title"
          data-reveal
        >
          <EngagementWheel />
          <div className={styles.engagementCopy}>
            <p className={styles.eyebrow}>UMA EXPERIÊNCIA QUE CONTINUA</p>
            <h2 id="engagement-title">
              Roleta da Sorte
              <br />
              <em>Programa de afiliados</em>
            </h2>
            <p>
              {premiumFeatures[1].description} {premiumFeatures[5].description}
            </p>
            <a
              href="#faq-question-2"
              className={styles.textButton}
              onClick={() => setOpenFaqIndex(2)}
            >
              Entenda a roleta e os afiliados <ArrowRightIcon />
            </a>
          </div>
        </section>
        <RestoredTestimonials />
        <section
          id="faq"
          className={`${styles.container} ${styles.faqSection}`}
          aria-labelledby="faq-title"
        >
          <div className={styles.faqIntro} data-reveal>
            <p className={styles.eyebrow}>{sorteeLandingFaq.tag}</p>
            <h2 id="faq-title">
              {sorteeLandingFaq.headingLead}
              <em>{sorteeLandingFaq.headingHighlight}</em>
            </h2>
            <p>{sorteeLandingFaq.description}</p>
            <a
              className={styles.textButton}
              href={sorteeLandingFooter.phone.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              Converse com nosso time <ArrowRightIcon />
            </a>
            <p className={styles.contactHint}>Atendimento pelo WhatsApp.</p>
          </div>
          <div className={styles.faqList}>
            {premiumFaq.map((item, index) => (
              <article
                className={styles.faqItem}
                key={item.question}
                data-open={openFaqIndex === index}
              >
                <h3>
                  <button
                    type="button"
                    id={`faq-question-${index}`}
                    className={styles.faqQuestion}
                    aria-expanded={openFaqIndex === index}
                    aria-controls={`faq-answer-${index}`}
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  >
                    <span className={styles.faqNumber}>0{index + 1}</span>
                    <span>{item.question}</span>
                    <span className={styles.faqToggle} aria-hidden="true">
                      <LandingIcon name="plus" />
                    </span>
                  </button>
                </h3>
                <div
                  id={`faq-answer-${index}`}
                  className={styles.faqAnswer}
                  role="region"
                  aria-labelledby={`faq-question-${index}`}
                  aria-hidden={openFaqIndex !== index}
                  inert={openFaqIndex !== index}
                >
                  <div className={styles.faqAnswerContent}>
                    <p>
                      <EmphasizedCopy text={item.answer} highlight={item.highlight} />
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
        <section className={styles.closing} aria-labelledby="closing-title">
          <div className={`${styles.container} ${styles.closingInner}`} data-reveal>
            <div>
              <p className={styles.eyebrow}>{sorteeLandingCta.tag}</p>
              <h2 id="closing-title">
                {sorteeLandingCta.headingLineOne}
                <br />
                <em>{sorteeLandingCta.headingLineTwo}</em>
              </h2>
            </div>
            <div className={styles.closingActions}>
              <Link className={styles.primaryButton} to="/cadastro">
                {sorteeLandingCta.primaryActionLabel} <ArrowRightIcon />
              </Link>
              <span>{sorteeLandingCta.description}</span>
              <a
                className={styles.textButton}
                href={sorteeLandingFooter.phone.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                Agendar demonstração <ArrowRightIcon />
              </a>
            </div>
            <span className={styles.closingStar} aria-hidden="true">
              <LandingIcon name="star" />
            </span>
          </div>
        </section>
      </main>
      <LandingFooter />
    </div>
  )
}
